import { useUIColors } from "@/core/hooks";
import { useI18nContext } from "@/core/providers/I18nProvider";
import React, { ReactNode, useRef, useState } from "react";
import {
  Platform,
  Pressable,
  TextInput as RNTextInput,
  TextInputProps as RNTextInputProps,
  StyleSheet,
  Text,
  View,
} from "react-native";

interface TextInputProps extends Omit<RNTextInputProps, "style"> {
  label?: string;
  error?: string;
  /** Overrides the default theme error text color (colors.fontErrorText) for this instance only. */
  errorTextColor?: string;
  disabled?: boolean;
  required?: boolean;
  containerStyle?: any;
  state?:
    | "unselected-placeholder"
    | "selected-empty"
    | "selected-filled"
    | "error"
    | "disabled"
    | "unselected-filled";
  variant?: "default" | "search";
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  inputStyle?: any;
  inputContainerStyle?: any;
}

export const TextInput = React.forwardRef<RNTextInput, TextInputProps>(
  (
    {
      label,
      error,
      errorTextColor,
      disabled = false,
      required = false,
      placeholder,
      value,
      onChangeText,
      containerStyle,
      inputContainerStyle,
      state,
      variant = "default",
      leftIcon,
      rightIcon,
      inputStyle,
      onFocus: customOnFocus,
      onBlur: customOnBlur,
      ...props
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const colors = useUIColors();
    const { isRTL } = useI18nContext();
    const inputRef = useRef<RNTextInput>(null);

    const isEmpty = !value || value.length === 0;
    const hasError = !!error;

    const getInputState = () => {
      if (state) return state;
      if (disabled) return "disabled";
      if (hasError) return "error";
      if (isFocused && isEmpty) return "selected-empty";
      if (isFocused && !isEmpty) return "selected-filled";
      if (!isEmpty) return "unselected-filled";
      return "unselected-placeholder";
    };

    const inputState = getInputState();

    const getBorderColor = () => {
      if (variant === "search") {
        return "#d6d6d6"; // Light gray border for search (from Figma)
      }

      switch (inputState) {
        case "error":
          return colors.outlineError; // Red from Figma
        case "selected-empty":
        case "selected-filled":
          // Use a visible color for focused state border
          // Figma focused state should have a visible border that contrasts with background
          return colors.backgroundLevel4;
        case "disabled":
          return colors.outlineInactive; // Inactive border for disabled
        case "unselected-filled":
        case "unselected-placeholder":
        default:
          return "transparent"; // No border for unselected states
      }
    };

    const getTextColor = () => {
      switch (inputState) {
        case "disabled":
          return "#999999"; // Disabled text color from Figma
        case "selected-empty":
          return "#cccccc"; // UI Colours/Font/Placeholder-lighter from Figma
        case "error":
        case "selected-filled":
        case "unselected-filled":
          return "#333333"; // UI Colours/Font/Primary from Figma
        case "unselected-placeholder":
        default:
          return "#b3b3b3"; // Placeholder color from Figma
      }
    };

    const getBackgroundColor = () => {
      if (variant === "search") {
        return colors.backgroundLevel1; // White background for search
      }
      return Platform.OS === "web" ? "#f2f2f2" : colors.backgroundLevel2;
    };

    const styles = StyleSheet.create({
      container: {
        gap: 8, // Spacing/2 from Figma
        width: "100%", // Ensure container takes full width for RTL alignment
        flexDirection: "column", // Make container a flex container so alignSelf works on children
      },
      labelContainer: {
        // Keep a consistent row direction; justifyContent applied inline for dynamic RTL updates.
        // Label/asterisk order is handled by conditional rendering in JSX, not flexDirection.
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: 0,
        // alignSelf and width applied inline to ensure proper RTL alignment
      },
      label: {
        fontSize: 14, // Font/Size/XS from Figma
        fontWeight: "500", // Font/weight/Medium from Figma
        color: colors.fontPrimary, // UI Colours/Font/Primary from Figma
        fontFamily: Platform.OS === "web" ? "Matter" : "Matter-Medium",
        lineHeight: 18,
        minHeight: 14,
        flexShrink: 0, // Prevent label text from being truncated
        flexGrow: 0, // Prevent label from expanding and allow justifyContent to work
        // textAlign is applied inline for dynamic RTL updates
      },
      required: {
        color: colors.fontPrimary,
        marginLeft: isRTL ? 0 : 2,
        marginRight: isRTL ? 2 : 0,
        fontFamily: Platform.OS === "web" ? "Matter" : "Matter-Medium",
        fontSize: 14,
        fontWeight: "500",
        lineHeight: 18,
      },
      inputContainer: {
        borderWidth: 1,
        borderColor: getBorderColor(),
        borderRadius: 8,
        backgroundColor: getBackgroundColor(),
        paddingHorizontal: 16, // Spacing/4 from Figma
        paddingVertical: 12, // Consistent padding for both platforms
        minHeight: 48,
        gap: 8,
        alignItems: "center",
        justifyContent: "center", // Center content vertically
        flexDirection: isRTL ? "row-reverse" : "row",
      },
      inputContainerMultiline: {
        alignItems: "flex-start",
      },
      input: {
        fontSize: 16, // Font/Size/Base from Figma
        fontWeight: "400", // Font/weight/Regular from Figma
        color: getTextColor(),
        fontFamily: "Matter",
        flex: 1,
        padding: 0,
        margin: 0, // Remove any default margins
        ...(Platform.OS === "ios"  
          ? {
              // iOS: lineHeight matches fontSize to match design
              lineHeight: 16, // Matches fontSize - prevents clipping and matches design
              // No height constraint - let iOS handle vertical spacing naturally
            }
          : {
              // Android styles
              height: 24,
              lineHeight: 24,
              includeFontPadding: false, // Remove Android font padding
              textAlignVertical: "center", // Center text vertically on Android
            }),
        ...(Platform.OS === "web"
          ? {
              // Remove default browser focus outline (blue box) on web
              outlineWidth: 0,
              outlineColor: "transparent",
            }
          : {}),
        textAlign: isRTL ? "right" : "left",
        writingDirection: isRTL ? "rtl" : "ltr",
      },
      errorText: {
        fontSize: 12,
        color: errorTextColor ?? colors.fontErrorText,
        marginTop: 4,
        textAlign: isRTL ? "right" : "left",
      },
    });

    TextInput.displayName = "TextInput";

    return (
      <View style={[styles.container, containerStyle]}>
        {label && (
          <View
            style={[
              styles.labelContainer,
              {
                alignSelf: "stretch",
                width: "100%",
                minWidth: 0,
              },
            ]}
          >
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                marginLeft: isRTL ? "auto" : 0,
                marginRight: isRTL ? 0 : "auto",
              }}
            >
              {isRTL ? (
                // RTL: label group is right-aligned, with the
                // mandatory asterisk visually to the LEFT of the text
                <>
                  {required && <Text style={styles.required}>*</Text>}
                  <Text style={[styles.label, { textAlign: "right" }]}>
                    {String(label)}
                  </Text>
                </>
              ) : (
                // LTR: label group is left-aligned, with the
                // mandatory asterisk to the RIGHT of the text
                <>
                  <Text style={[styles.label, { textAlign: "left" }]}>
                    {String(label)}
                  </Text>
                  {required && <Text style={styles.required}>*</Text>}
                </>
              )}
            </View>
          </View>
        )}

        <Pressable
          style={[
            styles.inputContainer,
            props.multiline ? styles.inputContainerMultiline : null,
            inputContainerStyle,
          ]}
          onPress={() => {
            // Make entire container tappable - focus input when container is tapped
            // Use internal ref which is always set
            if (inputRef.current) {
              inputRef.current.focus();
            }
          }}
        >
          {/* Left icon in LTR, Right icon in RTL */}
          {isRTL ? rightIcon : leftIcon}

          <RNTextInput
            ref={(node) => {
              // Handle both forwarded ref and internal ref
              if (ref) {
                if (typeof ref === "function") {
                  ref(node);
                } else if ("current" in ref) {
                  (ref as React.RefObject<RNTextInput | null>).current =
                    node;
                }
              }
              inputRef.current = node;
            }}
            style={[
              styles.input,
              props.multiline
                ? {
                    minHeight: Math.max(110, (props.numberOfLines ?? 4) * 24),
                    textAlignVertical: "top",
                  }
                : null,
              inputStyle,
            ]}
            placeholder={placeholder ? String(placeholder) : undefined}
            placeholderTextColor={colors.fontTertiary}
            selectionHandleColor={colors.fontSecondary}
            selectionColor={colors.backgroundLevel4}
            cursorColor={colors.fontPrimary} // Web cursor color - dark primary text color
            value={value}
            onChangeText={onChangeText}
            onFocus={(e) => {
              setIsFocused(true);
              customOnFocus?.(e); // Call custom onFocus handler if provided
            }}
            onBlur={(e) => {
              setIsFocused(false);
              customOnBlur?.(e); // Call custom onBlur handler if provided
            }}
            editable={!disabled}
            selectTextOnFocus={!disabled}
            scrollEnabled={props.multiline ? true : false}
            multiline={props.multiline ?? false}
            {...props}
          />

          {/* Right icon in LTR, Left icon in RTL */}
          {isRTL ? leftIcon : rightIcon}
        </Pressable>

        {hasError && error ? (
          <Text style={styles.errorText}>{String(error)}</Text>
        ) : null}
      </View>
    );
  },
);

export default TextInput;
